import { CommitmentTypes } from '../model/enums'
import type {
  AcknowledgmentCommitmentItem,
  AcknowledgmentCommitmentSummary,
  AcknowledgmentPrimaryAction,
  AcknowledgmentStatusViewModel,
} from '../model/types'

/* Signers of one commitment type keep the order the procedure returns them in (the sort is
   stable). */
const compareByPriority = (
  left: AcknowledgmentCommitmentItem,
  right: AcknowledgmentCommitmentItem
) =>
  (left.CommitmentOrder ?? Number.MAX_SAFE_INTEGER) -
  (right.CommitmentOrder ?? Number.MAX_SAFE_INTEGER)

const isSameEmail = (left?: string | null, right?: string | null) =>
  left?.trim().toLowerCase() === right?.trim().toLowerCase()

export const buildAcknowledgmentStatusView = ({
  items,
}: {
  items: AcknowledgmentCommitmentItem[]
}): AcknowledgmentStatusViewModel => {
  const sortedItems = [...items].sort(compareByPriority)
  const groupsMap = new Map<string, AcknowledgmentCommitmentItem[]>()

  sortedItems.forEach((item) => {
    const key = item.CommitmentTypeName || ''

    if (!groupsMap.has(key)) {
      groupsMap.set(key, [])
    }

    groupsMap.get(key)?.push(item)
  })

  const summaries: AcknowledgmentCommitmentSummary[] = Array.from(groupsMap.entries())
    .map(([typeName, groupItems]) => ({
      typeName,
      commitmentType: groupItems[0]?.CommitmentType ?? CommitmentTypes.Partner,
      signedCount: groupItems.filter((item) => item.IsAcknowledged === 1).length,
      totalCount: groupItems.length,
    }))
    .sort((left, right) => {
      const leftItem = groupsMap.get(left.typeName)?.[0]
      const rightItem = groupsMap.get(right.typeName)?.[0]

      if (!leftItem || !rightItem) {
        return 0
      }

      return compareByPriority(leftItem, rightItem)
    })

  /* The procedure may hand the flag over as a number, a string or a bit, so any positive value
     marks the next signer. */
  const nextSigner =
    sortedItems.find((item) => Number(item.SignoffPending) > 0) ??
    sortedItems.find((item) => item.CanResend > 0) ??
    null

  const primaryAction: AcknowledgmentPrimaryAction = nextSigner
    ? { kind: 'notify', item: nextSigner }
    : null

  const itemsByType: Record<string, AcknowledgmentCommitmentItem[]> = {}

  groupsMap.forEach((groupItems, typeName) => {
    itemsByType[typeName] = groupItems
  })

  return {
    items: sortedItems,
    itemsByType,
    summaries,
    primaryAction,
    signedCount: sortedItems.filter((item) => item.IsAcknowledged === 1).length,
    totalCount: sortedItems.length,
  }
}

export const isCurrentUserCommitment = (
  item: AcknowledgmentCommitmentItem,
  currentUserEmail: string
) => isSameEmail(item.SignerEmail, currentUserEmail)
